import * as Sentry from "@sentry/react";
import type { Mutation, Query } from "@tanstack/react-query";

/**
 * Environnement remonté à Sentry.
 *
 * PROD et DEMO sont deux builds `vite build` : `MODE` y vaut « production » dans les
 * deux cas, et les erreurs du site de démonstration se mélangeaient à celles de la
 * production. `VITE_SENTRY_ENVIRONMENT` (Vercel) fait foi ; à défaut, `VITE_IS_DEMO`
 * suffit à reconnaître la démo.
 */
export function sentryEnvironment(): string {
  const explicit = import.meta.env.VITE_SENTRY_ENVIRONMENT;
  if (explicit) return explicit;
  if (import.meta.env.VITE_IS_DEMO === "true") return "demo";
  return import.meta.env.MODE;
}

/** Utilisateur courant : l'identifiant seul, jamais l'email ni le nom (décision D6). */
export function setSentryUser(userId: string | null): void {
  Sentry.setUser(userId ? { id: userId } : null);
}

/**
 * Dernière organisation ouverte (`/club/:orgId`, `/association/:orgId`). Elle reste
 * posée quand on quitte le tableau de bord : une erreur dans la gestion d'un tournoi
 * ou le planning se rattache ainsi au club qui testait — d'où « last ».
 */
export function setSentryOrganization(orgId: string): void {
  Sentry.setTag("last_organization_id", orgId);
}

function queryKeyLabel(key: readonly unknown[] | undefined): string {
  const head = key?.[0];
  return typeof head === "string" ? head : "unknown";
}

function toError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (error && typeof error === "object" && "message" in error) {
    return new Error(String((error as { message: unknown }).message));
  }
  return new Error(typeof error === "string" ? error : "Erreur inconnue");
}

/**
 * La plupart des échecs de l'application sont rattrapés par les hooks et finissent en
 * toast : sans ce relais, Sentry ne voyait que les plantages de rendu. Branché sur
 * `QueryCache` / `MutationCache` (src/App.tsx), il voit toute requête et toute
 * mutation en échec, que le hook ait son propre `onError` ou non.
 */
export function reportQueryError(
  error: unknown,
  kind: "query" | "mutation",
  key: readonly unknown[] | undefined
): void {
  if (error instanceof DOMException && error.name === "AbortError") return;

  const err = toError(error);
  const pg = error as { code?: unknown; details?: unknown; hint?: unknown };
  const httpStatus = (error as { context?: { status?: unknown } }).context?.status;
  const code = typeof pg.code === "string" ? pg.code : undefined;
  const label = queryKeyLabel(key);

  Sentry.withScope((scope) => {
    scope.setTag("react_query", kind);
    scope.setTag("query_key", label);
    if (code) scope.setTag("pg_code", code);
    if (typeof httpStatus === "number") scope.setTag("http_status", String(httpStatus));
    scope.setContext("supabase", {
      code,
      details: pg.details,
      hint: pg.hint,
      httpStatus,
    });
    // P0001 = RAISE EXCEPTION d'une règle métier (quota atteint, place prise…) :
    // un message destiné au joueur, utile à voir mais pas un défaut du code.
    scope.setLevel(code === "P0001" ? "warning" : "error");
    // Une même requête qui échoue pour la même raison = un seul ticket. Le message
    // reste dans l'empreinte : la plupart des mutations n'ont pas de clé, et seul le
    // texte de PostgREST (table, contrainte, policy) distingue leurs échecs.
    scope.setFingerprint(["react-query", kind, label, code ?? "-", err.message]);
    Sentry.captureException(err);
  });
}

export const onQueryError = (error: unknown, query: Query<unknown, unknown, unknown>) =>
  reportQueryError(error, "query", query.queryKey);

export const onMutationError = (
  error: unknown,
  _variables: unknown,
  _context: unknown,
  mutation: Mutation<unknown, unknown, unknown>
) => reportQueryError(error, "mutation", mutation.options.mutationKey);
